import * as ww from '@wecom/jssdk'
import wecomApi from '@/api/modules/wecom'
/**
 * 企业微信工具类
 */

const WECOM_DEBUG_KEY = 'wecom_debug'

// 配置状态
let isRegistered = false
let isConfigReady = false
let corpId = ''
let agentId = ''

function maskValue(value: string | null | undefined, keep = 4): string {
  if (!value) {
    return ''
  }
  if (value.length <= keep * 2) {
    return `${value.slice(0, 1)}***`
  }
  return `${value.slice(0, keep)}***${value.slice(-keep)}`
}

function readDebugFlagFromQuery(): boolean | null {
  try {
    const value = new URLSearchParams(window.location.search).get(WECOM_DEBUG_KEY)
    if (value === null) {
      return null
    }
    return value === '1' || value.toLowerCase() === 'true'
  }
  catch {
    return null
  }
}

export function isWecomDebugEnabled(): boolean {
  const queryFlag = readDebugFlagFromQuery()
  if (queryFlag !== null) {
    try {
      localStorage.setItem(WECOM_DEBUG_KEY, queryFlag ? '1' : '0')
    }
    catch {
    }
    return queryFlag
  }

  try {
    const localFlag = localStorage.getItem(WECOM_DEBUG_KEY)
    return localFlag === '1' || localFlag?.toLowerCase() === 'true'
  }
  catch {
    return false
  }
}

export function setWecomDebugEnabled(enabled: boolean) {
  try {
    localStorage.setItem(WECOM_DEBUG_KEY, enabled ? '1' : '0')
  }
  catch {
  }
}

export function logWecomDebug(step: string, payload?: Record<string, any>) {
  if (!isWecomDebugEnabled()) {
    return
  }
  console.warn(`[WECOM-DEBUG] ${step}`, payload || {})
}

export type WecomConversationType = 'external_contact' | 'external_chat'

/**
 * 检查是否在企业微信环境中
 */
export function isWecomEnvironment(): boolean {
  const ua = navigator.userAgent
  const uaLower = ua.toLowerCase()
  const inWecom = uaLower.includes('wxwork')
  logWecomDebug('isWecomEnvironment', {
    inWecom,
    ua: ua.slice(0, 160),
    href: window.location.href,
  })
  return inWecom
}

/**
 * 注册企业微信 JS-SDK
 */
export async function registerWecomJsSDK(): Promise<boolean> {
  logWecomDebug('registerWecomJsSDK:start', {
    isRegistered,
    isConfigReady,
    href: window.location.href,
  })
  if (isRegistered) {
    logWecomDebug('registerWecomJsSDK:reuse', { isConfigReady })
    return isConfigReady
  }

  try {
    const response = await wecomApi.getConfig()
    const initial = response.data
    logWecomDebug('registerWecomJsSDK:getConfig:ok', {
      appid: maskValue(initial?.appid),
      agentid: maskValue(initial?.agentid),
    })

    if (!initial || !initial.appid) {
      console.warn('企业微信未配置或配置无效')
      logWecomDebug('registerWecomJsSDK:getConfig:invalid', { initial })
      return false
    }

    corpId = initial.appid
    agentId = initial.agentid || ''

    // 注册 SDK：提供获取签名的方法（按 URL 动态获取）
    ww.register({
      corpId: initial.appid,
      agentId: initial.agentid,
      jsApiList: [
        'getContext',
        'login',
        'getCurExternalContact',
        'getCurExternalChat',
        'scanQRCode',
        'selectEnterpriseContact',
        'selectExternalContact',
        'openEnterpriseChat',
        'openUserProfile',
        'openExistedChatWithMsg',
        'selectPrivilegedContact',
        'createChatWithMsg',
        'createCorpGroupChat',
      ],
      async getConfigSignature(url: string) {
        logWecomDebug('registerWecomJsSDK:getConfigSignature:req', {
          url,
        })
        const resp = await wecomApi.getConfigSignature(url)
        const cfg = resp.data
        logWecomDebug('registerWecomJsSDK:getConfigSignature:resp', {
          timestamp: cfg.timestamp,
          nonceStr: maskValue(cfg.nonceStr),
          signature: maskValue(cfg.signature),
        })
        return {
          timestamp: cfg.timestamp,
          nonceStr: cfg.nonceStr,
          signature: cfg.signature,
        }
      },
      async getAgentConfigSignature(url: string) {
        logWecomDebug('registerWecomJsSDK:getAgentConfigSignature:req', {
          url,
        })
        const resp = await wecomApi.getConfigSignature(url)
        const cfg = resp.data
        logWecomDebug('registerWecomJsSDK:getAgentConfigSignature:resp', {
          timestamp: cfg.timestamp,
          nonceStr: maskValue(cfg.nonceStr),
          signature: maskValue(cfg.agentSignature || cfg.signature),
        })
        return {
          timestamp: cfg.timestamp,
          nonceStr: cfg.nonceStr,
          signature: cfg.agentSignature || cfg.signature,
        }
      },
      onConfigSuccess() {
        logWecomDebug('registerWecomJsSDK:onConfigSuccess')
        isConfigReady = true
      },
      onConfigFail(err: any) {
        console.error('企业微信 JS-SDK 配置失败:', err)
        logWecomDebug('registerWecomJsSDK:onConfigFail', {
          errMsg: err?.errMsg || err?.err_msg || err?.message,
          err,
        })
        isConfigReady = false
      },
    })

    await ww.ensureConfigReady()
    logWecomDebug('registerWecomJsSDK:ensureConfigReady:ok')
    isRegistered = true
    isConfigReady = true
    return true
  }
  catch (err: any) {
    console.error('注册企业微信 JS-SDK 失败:', err)
    logWecomDebug('registerWecomJsSDK:failed', {
      errMsg: err?.errMsg || err?.err_msg || err?.message,
      err,
    })
    isRegistered = false
    isConfigReady = false
    return false
  }
}

/**
 * 打开成员资料页（用于联系群主/管理员）
 */
export async function openWecomUserProfile(params: { userid: string, type?: 1 | 2 }): Promise<void> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }
  const userid = String(params.userid || '').trim()
  if (!userid) {
    throw new Error('userid不能为空')
  }

  await ww.openUserProfile({
    type: params.type || 1,
    userid,
  })
}

/**
 * 选择企业微信外部联系人（客户）
 */
export async function selectWecomCustomers(): Promise<string[]> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  const res: any = await ww.selectExternalContact({
    filterType: 0,
  })

  const ids = (res?.userIds || []) as string[]
  return Array.isArray(ids) ? ids : []
}

/**
 * 选择企业内部成员
 */
export interface WecomSelectedInternalUser {
  id: string
  name?: string
  avatar?: string
}

export async function selectWecomInternalUsersDetailed(params?: {
  selectedUserIds?: string[]
  mode?: 'single' | 'multi'
}): Promise<WecomSelectedInternalUser[]> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  const res: any = await ww.selectEnterpriseContact({
    fromDepartmentId: 0,
    mode: params?.mode === 'single' ? ww.SelectEnterpriseContactMode.single : ww.SelectEnterpriseContactMode.multi,
    type: [ww.SelectEnterpriseContactType.user],
    selectedUserIds: params?.selectedUserIds || [],
  })

  const users = (res?.result?.userList || [])
    .map((u: any) => ({
      id: String(u?.id || '').trim(),
      name: String(u?.name || '').trim() || undefined,
      avatar: String(u?.avatar || '').trim() || undefined,
    }))
    .filter((u: any) => Boolean(u.id)) as WecomSelectedInternalUser[]

  return Array.isArray(users) ? users : []
}

export async function selectWecomInternalUsers(params?: {
  selectedUserIds?: string[]
  mode?: 'single' | 'multi'
}): Promise<string[]> {
  const users = await selectWecomInternalUsersDetailed(params)
  return users.map(u => u.id)
}

/**
 * 打开/创建客户群聊
 * - 传 chatId：打开已有客户群
 * - 传 externalUserIds：创建会话
 */
export async function openWecomCustomerChat(params: {
  chatId?: string
  groupName?: string
  externalUserIds?: string[]
  userIds?: string[]
}): Promise<{ chatId?: string }> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }
  if (import.meta.env.DEV) {
    console.warn('openWecomCustomerChat params:', params)
  }
  const res: any = await ww.openEnterpriseChat({
    chatId: params.chatId,
    groupName: params.groupName,
    externalUserIds: params.externalUserIds,
    userIds: params.userIds,
  })

  return { chatId: res?.chatId }
}

/**
 * 创建企业互联/上下游会话（可包含外部联系人）
 * 文档：https://developer.work.weixin.qq.com/document/path/96936
 */
export async function createWecomCorpGroupChat(params: {
  groupName?: string
  externalUserIds?: string[]
  userIds?: string[]
  openUserIds?: string[]
  corpGroupUserIds?: Array<{
    corpId: string
    userId?: string
    openUserId?: string
  }>
}): Promise<{ chatId: string }> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  const res: any = await ww.createCorpGroupChat({
    groupName: params.groupName,
    externalUserIds: params.externalUserIds,
    userIds: params.userIds,
    openUserIds: params.openUserIds,
    corpGroupUserIds: params.corpGroupUserIds,
  })

  const chatId = String(res?.chatId || '')
  return { chatId }
}

/**
 * 获取企业微信配置状态
 */
export function getWecomConfig() {
  return {
    isRegistered,
    isConfigReady,
    corpId,
    agentId,
  }
}

/**
 * 调用企业微信 JS-SDK 的 login 方法
 */
export async function invokeWecomLogin(): Promise<string> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  logWecomDebug('invokeWecomLogin:start')
  return new Promise((resolve, reject) => {
    ww.invoke('login', {})
      .then((res: any) => {
        logWecomDebug('invokeWecomLogin:response', {
          errMsg: res?.errMsg || res?.err_msg,
          code: maskValue(res?.code),
          res,
        })
        const ok = res?.errMsg === 'login:ok' || res?.err_msg === 'login:ok'
        if (ok && res?.code) {
          resolve(res.code)
        }
        else {
          reject(new Error(res?.errMsg || res?.err_msg || '登录失败'))
        }
      })
      .catch((err: any) => {
        logWecomDebug('invokeWecomLogin:catch', {
          errMsg: err?.errMsg || err?.err_msg || err?.message,
          err,
        })
        reject(err)
      })
  })
}

/**
 * 创建企业微信登录面板
 */
export function createWecomLoginPanel(
  el: HTMLElement,
  params: any,
  callbacks: {
    onLoginSuccess?: (res: any) => void
    onLoginFail?: (err: any) => void
    onCheckWeComLogin?: () => void
  },
) {
  logWecomDebug('createWecomLoginPanel', {
    appid: maskValue(params?.appid),
    agentid: maskValue(params?.agentid),
    state: params?.state,
    redirect_uri: params?.redirect_uri,
  })
  return ww.createWWLoginPanel({
    el,
    params,
    onLoginSuccess: callbacks.onLoginSuccess,
    onLoginFail: callbacks.onLoginFail,
    onCheckWeComLogin: callbacks.onCheckWeComLogin,
  })
}

/**
 * 获取当前上下文信息
 */
export async function getWecomContext(): Promise<any> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  return ww.invoke('getContext', {})
}

export function resolveWecomErrorMessage(error: any): string {
  return String(error?.errMsg || error?.err_msg || error?.message || '').trim()
}

export function isWecomContextUnsupportedError(error: any): boolean {
  return resolveWecomErrorMessage(error).includes('without context of external contact')
}

export async function getCurrentExternalContactId(): Promise<string> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  const result = await ww.getCurExternalContact()
  const userId = String(result?.userId || '').trim()
  if (userId === '') {
    throw new Error('未获取到当前私聊客户ID')
  }

  return userId
}

export async function getCurrentExternalChatId(): Promise<string> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  const result = await ww.getCurExternalChat()
  const chatId = String(result?.chatId || '').trim()
  if (chatId === '') {
    throw new Error('未获取到当前群聊ID')
  }

  return chatId
}

export async function detectCurrentWecomConversation(): Promise<{
  type: WecomConversationType
  id: string
}> {
  try {
    const externalContactId = await getCurrentExternalContactId()
    return {
      type: 'external_contact',
      id: externalContactId,
    }
  }
  catch (externalContactError) {
    if (!isWecomContextUnsupportedError(externalContactError)) {
      throw externalContactError
    }
  }

  const externalChatId = await getCurrentExternalChatId()
  return {
    type: 'external_chat',
    id: externalChatId,
  }
}

/**
 * 调起企业微信扫码，并返回扫码内容
 */
export async function scanWecomQRCode(params?: {
  needResult?: boolean
  scanType?: ww.ScanQRCodeType[]
}): Promise<string> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  const result = await ww.scanQRCode({
    needResult: params?.needResult ?? true,
    scanType: params?.scanType ?? [ww.ScanQRCodeType.qrCode, ww.ScanQRCodeType.barCode],
  })

  return String(result?.resultStr || '').trim()
}

/**
 * 重置配置状态（用于测试或重新初始化）
 */
export function resetWecomConfig() {
  logWecomDebug('resetWecomConfig')
  isRegistered = false
  isConfigReady = false
  corpId = ''
  agentId = ''
}

/**
 * 从 URL 中获取企业微信授权 code 和 state
 */
export function getWecomCodeFromUrl(): { code: string | null, state: string | null } {
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const state = urlParams.get('state')

  logWecomDebug('getWecomCodeFromUrl', {
    code: maskValue(code),
    state,
  })
  return { code, state }
}

/**
 * 检查 URL 中是否有企业微信授权 code
 */
export function hasWecomCode(): boolean {
  const { code, state } = getWecomCodeFromUrl()
  return !!(code && state)
}

/**
 * 清除 URL 中的企业微信授权相关参数
 */
export function cleanWecomUrlParams() {
  const url = new URL(window.location.href)
  const params = url.searchParams

  // 移除授权相关参数
  params.delete('code')
  params.delete('state')
  params.delete('wecom_bind')

  // 构建新的 URL（保留 hash）
  const newUrl = `${url.origin}${url.pathname}${params.toString() ? `?${params.toString()}` : ''}${url.hash}`

  // 使用 replaceState 替换 URL，不会触发页面刷新
  logWecomDebug('cleanWecomUrlParams', {
    from: window.location.href,
    to: newUrl,
  })
  window.history.replaceState({}, document.title, newUrl)
}

/**
 * 构建 OAuth2 授权 URL
 */
export async function buildOAuth2Url(
  getParamsFn: (state: string) => Promise<any>,
  redirectUri?: string,
): Promise<string> {
  const suggestedState = `wecom_login_${Date.now()}`
  const response = await getParamsFn(suggestedState)
  const params = response.data || {}
  const state = String(params.state || suggestedState)
  const resolvedRedirectUri = redirectUri || params.redirect_uri || window.location.href

  const authUrl = new URL('https://open.weixin.qq.com/connect/oauth2/authorize')
  authUrl.searchParams.set('appid', params.appid)
  authUrl.searchParams.set('redirect_uri', resolvedRedirectUri)
  authUrl.searchParams.set('response_type', 'code')
  authUrl.searchParams.set('scope', params.scope || 'snsapi_base')
  authUrl.searchParams.set('state', state)
  authUrl.hash = 'wechat_redirect'

  logWecomDebug('buildOAuth2Url', {
    appid: maskValue(params.appid),
    agentid: maskValue(params.agentid),
    state,
    redirect_uri: resolvedRedirectUri,
    authUrl: authUrl.toString(),
  })
  return authUrl.toString()
}

/**
 * 跳转到 OAuth2 授权页面
 */
export async function redirectToOAuth2(
  getParamsFn: (state: string) => Promise<any>,
  redirectUri?: string,
) {
  const url = await buildOAuth2Url(getParamsFn, redirectUri)
  logWecomDebug('redirectToOAuth2', { url })
  window.location.href = url
}

/**
 * 检查 URL 参数中是否有绑定标记
 */
export function hasWecomBindFlag(): boolean {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('wecom_bind') === '1'
}

/**
 * 添加绑定标记到 URL
 */
export function addWecomBindFlag(url: string): string {
  const urlObj = new URL(url)
  urlObj.searchParams.set('wecom_bind', '1')
  return urlObj.toString()
}

/**
 * 处理企业微信授权回调自动登录
 * @param loginFn 登录函数，接收 code 和 state，返回登录响应
 * @returns 登录结果，如果没有 code 则返回 null
 */
export async function handleWecomAutoLogin(
  loginFn: (code: string, state: string) => Promise<any>,
): Promise<any | null> {
  const { code, state } = getWecomCodeFromUrl()

  if (!code || !state) {
    return null
  }

  try {
    // 调用登录函数
    const result = await loginFn(code, state)

    // 清除 URL 中的 code 和 state 参数
    cleanWecomUrlParams()

    return result
  }
  catch (err) {
    // 登录失败，清除 URL 参数
    cleanWecomUrlParams()
    throw err
  }
}
